export const SUBSCRIPTION_STATUS = {
  TRIAL_EXPIRED: 'trial_expired',
  TRIALING: 'trialing',
  OK: 'ok',
  UNPAID: 'unpaid',
  PAID: 'paid',
  CANCELED: 'canceled',
  WAITING_PAYMENT: 'waiting_payment',
  PENDING_PAYMENT: 'pending_payment',
};

export const REGULARIZATION_INELIGIBILITY_REASONS = {
  ALREADY_REGULARIZED: 'already_regularized',
  NOT_IN_REGULARIZATION_PERIOD: 'not_in_regularization_period',
  CANCELED: 'canceled',
  NO_STATUS: 'no_status',
};

const REGULARIZATION_STATUSES = new Set([
  SUBSCRIPTION_STATUS.WAITING_PAYMENT,
  SUBSCRIPTION_STATUS.PENDING_PAYMENT,
]);

const REGULARIZED_STATUSES = new Set([
  SUBSCRIPTION_STATUS.OK,
  SUBSCRIPTION_STATUS.PAID,
]);

const PAYMENT_METHOD_ALIASES = {
  creditcard: 'credit_card',
  billet: 'boleto',
};

export const normalizeSubscriptionPaymentMethod = (paymentMethod) => {
  if (!paymentMethod) return paymentMethod;
  const normalized = String(paymentMethod).toLowerCase();
  return PAYMENT_METHOD_ALIASES[normalized] ?? normalized;
};

export const normalizeSubscriptionStatus = (status) => {
  switch (String(status ?? '').toLowerCase()) {
    case 'active':
      return SUBSCRIPTION_STATUS.OK;
    case 'trialing':
      return SUBSCRIPTION_STATUS.TRIALING;
    case 'canceled':
    case 'cancelled':
      return SUBSCRIPTION_STATUS.CANCELED;
    case 'unpaid':
      return SUBSCRIPTION_STATUS.UNPAID;
    case 'waiting_payment':
      return SUBSCRIPTION_STATUS.WAITING_PAYMENT;
    case 'pending_payment':
      return SUBSCRIPTION_STATUS.PENDING_PAYMENT;
    case 'paid':
      return SUBSCRIPTION_STATUS.PAID;
    default:
      return SUBSCRIPTION_STATUS.UNPAID;
  }
};

export const computeIsExpired = (currentPeriodEnd) => {
  if (!currentPeriodEnd) {
    return false;
  }

  const end = new Date(currentPeriodEnd);

  if (Number.isNaN(end.getTime())) {
    return false;
  }

  return end.getTime() < Date.now();
};

export const deriveStatusFlags = (subscriptionInfo, isExpired) => {
  const isOk = subscriptionInfo === SUBSCRIPTION_STATUS.OK;
  const isPaid = subscriptionInfo === SUBSCRIPTION_STATUS.PAID;
  const isTrialing = subscriptionInfo === SUBSCRIPTION_STATUS.TRIALING;
  const isCanceled = subscriptionInfo === SUBSCRIPTION_STATUS.CANCELED;
  const isTrialExpired = subscriptionInfo === SUBSCRIPTION_STATUS.TRIAL_EXPIRED;
  const isUnpaid = subscriptionInfo === SUBSCRIPTION_STATUS.UNPAID;
  const isWaitingPayment = subscriptionInfo === SUBSCRIPTION_STATUS.WAITING_PAYMENT;
  const isPendingPayment = subscriptionInfo === SUBSCRIPTION_STATUS.PENDING_PAYMENT;

  return {
    isOk,
    isPaid,
    isTrialing,
    isCanceled,
    isTrialExpired,
    isUnpaid,
    isWaitingPayment,
    isPendingPayment,
    isFailed: false,
    isActive: (isOk || isPaid || isTrialing) && !isExpired,
    isInactive: isCanceled || isExpired,
  };
};

export const resolveRegularizationEligibility = ({
  subscriptionInfo,
  paymentMethod,
  isCanceled = false,
}) => {
  if (isCanceled || subscriptionInfo === SUBSCRIPTION_STATUS.CANCELED) {
    return {
      canRegularize: false,
      canRegularizeWithCard: false,
      reason: REGULARIZATION_INELIGIBILITY_REASONS.CANCELED,
    };
  }

  if (REGULARIZED_STATUSES.has(subscriptionInfo)) {
    return {
      canRegularize: false,
      canRegularizeWithCard: false,
      reason: REGULARIZATION_INELIGIBILITY_REASONS.ALREADY_REGULARIZED,
    };
  }

  if (!subscriptionInfo) {
    return {
      canRegularize: false,
      canRegularizeWithCard: false,
      reason: REGULARIZATION_INELIGIBILITY_REASONS.NO_STATUS,
    };
  }

  if (!REGULARIZATION_STATUSES.has(subscriptionInfo)) {
    return {
      canRegularize: false,
      canRegularizeWithCard: false,
      reason: REGULARIZATION_INELIGIBILITY_REASONS.NOT_IN_REGULARIZATION_PERIOD,
    };
  }

  return {
    canRegularize: true,
    canRegularizeWithCard: String(paymentMethod ?? '').toLowerCase() === 'credit_card',
    reason: null,
  };
};

const toUtcDateOnly = (date) =>
  new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()));

const addUtcDays = (date, days) => {
  const result = new Date(date);
  result.setUTCDate(result.getUTCDate() + days);
  return result;
};

export const computeNextChargeDate = (currentPeriodEnd, isActive) => {
  if (!isActive || !currentPeriodEnd) {
    return null;
  }

  const end = new Date(currentPeriodEnd);

  if (Number.isNaN(end.getTime())) {
    return null;
  }

  return addUtcDays(toUtcDateOnly(end), 1);
};

export const buildDerivedFields = ({ rawStatus, currentPeriodEnd, paymentMethod }) => {
  const subscriptionInfo = normalizeSubscriptionStatus(rawStatus);
  const isExpired = computeIsExpired(currentPeriodEnd);
  const statusFlags = deriveStatusFlags(subscriptionInfo, isExpired);
  const regularization = resolveRegularizationEligibility({
    subscriptionInfo,
    paymentMethod,
    isCanceled: statusFlags.isCanceled,
  });

  return {
    subscription_info: subscriptionInfo,
    isExpired,
    ...statusFlags,
    regularization,
    next_charge_date: computeNextChargeDate(currentPeriodEnd, statusFlags.isActive),
  };
};
